import { useEffect, useState } from "react";
import axios from "axios";
import { Compass, Briefcase, IndianRupee, RefreshCw, PlayCircle, Eye, ToggleLeft, ToggleRight, CheckCircle2, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import "./crm.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("asg_token")}` } });

const VSTYLE = {
  yatra: { color: "#B45309", bg: "#FEF3C7", icon: <Compass size={16} />, label: "Yatra" },
  jobs:  { color: "#1D4ED8", bg: "#DBEAFE", icon: <Briefcase size={16} />, label: "Jobs" },
  loans: { color: "#0F7A4E", bg: "#D1FAE5", icon: <IndianRupee size={16} />, label: "Loans" },
};

export default function SyncTab() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/crm/sync/configs`, authH()); setConfigs(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="crm-empty" data-testid="sync-loading"><Loader2 size={22} /> Loading sync settings…</div>;

  return (
    <div className="crm" data-testid="sync-tab">
      <div className="crm-head">
        <div>
          <h2>Google Sheets Sync</h2>
          <p>Import leads from any Google Sheet (shared as "Anyone with link · Viewer"). Duplicate detection on mobile + email.</p>
        </div>
        <button className="crm-refresh" onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="sync-help" data-testid="sync-help">
        <b>How it works</b>
        <ol>
          <li>Open your Google Sheet · <em>Share</em> · change access to <b>"Anyone with the link · Viewer"</b>.</li>
          <li>Copy the sheet URL (or just the ID) and paste it below for the relevant vertical.</li>
          <li>Set the worksheet/tab name (leave blank to use the first tab) and adjust the column mapping so it matches your sheet's headers.</li>
          <li>Click <b>Preview</b> to test — no data is written. Click <b>Sync now</b> to import. Turn <b>Daily sync</b> on to auto-import once every 24 hours.</li>
        </ol>
      </div>

      <div className="sync-grid" data-testid="sync-grid">
        {configs.map(cfg => <SyncCard key={cfg.vertical} config={cfg} onChanged={load} />)}
      </div>
    </div>
  );
}

function SyncCard({ config, onChanged }) {
  const style = VSTYLE[config.vertical];
  const [local, setLocal] = useState(config);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [dirty, setDirty] = useState(false);

  const patch = (k, v) => { setLocal(l => ({ ...l, [k]: v })); setDirty(true); };
  const patchMap = (col, header) => {
    setLocal(l => ({ ...l, column_mapping: { ...l.column_mapping, [col]: header } }));
    setDirty(true);
  };

  const save = async () => {
    setBusy("save"); setErr("");
    try {
      const body = { spreadsheet_id: local.spreadsheet_id, worksheet_name: local.worksheet_name,
                     column_mapping: local.column_mapping, source_label: local.source_label,
                     daily_sync_enabled: local.daily_sync_enabled };
      const r = await axios.patch(`${API}/admin/crm/sync/configs/${config.vertical}`, body, authH());
      setLocal(r.data); setDirty(false); onChanged?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };

  const toggleDaily = async () => {
    setBusy("toggle"); setErr("");
    try {
      const r = await axios.patch(`${API}/admin/crm/sync/configs/${config.vertical}`,
        { daily_sync_enabled: !local.daily_sync_enabled }, authH());
      setLocal(r.data); onChanged?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Toggle failed"); }
    finally { setBusy(""); }
  };

  const run = async (dryRun) => {
    if (dirty) { setErr("Save your changes first"); return; }
    setBusy(dryRun ? "preview" : "run"); setErr(""); setResult(null);
    try {
      const r = await axios.post(`${API}/admin/crm/sync/${config.vertical}/run?dry_run=${dryRun}`, {}, authH());
      setResult(r.data);
      if (!dryRun) { const c = await axios.get(`${API}/admin/crm/sync/configs/${config.vertical}`, authH()); setLocal(c.data); onChanged?.(); }
    } catch (e) { setErr(e?.response?.data?.detail || "Sync failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="sync-card" style={{ "--_c": style.color, "--_bg": style.bg }} data-testid={`sync-card-${config.vertical}`}>
      <div className="sync-card-head">
        <span className="crm-lead-tag" style={{ background: style.bg, color: style.color }}>{style.icon} {style.label}</span>
        <button className={`sync-toggle ${local.daily_sync_enabled ? "on" : ""}`} onClick={toggleDaily}
                disabled={busy === "toggle"} data-testid={`sync-toggle-${config.vertical}`}>
          {local.daily_sync_enabled ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
          <span>Daily sync {local.daily_sync_enabled ? "ON" : "OFF"}</span>
        </button>
      </div>

      {err && <div className="crm-form-error" data-testid={`sync-error-${config.vertical}`}>{err}</div>}

      <label>Spreadsheet URL or ID</label>
      <input value={local.spreadsheet_id || ""} onChange={e => patch("spreadsheet_id", e.target.value)}
             placeholder="Paste Google Sheet URL or ID" data-testid={`sync-sheet-${config.vertical}`} />

      <div className="sync-row">
        <div>
          <label>Worksheet / tab name</label>
          <input value={local.worksheet_name || ""} onChange={e => patch("worksheet_name", e.target.value)}
                 placeholder="e.g. Leads (leave blank for first tab)" data-testid={`sync-tab-${config.vertical}`} />
        </div>
        <div>
          <label>Lead source label</label>
          <input value={local.source_label || ""} onChange={e => patch("source_label", e.target.value)}
                 placeholder="Google Sheet" data-testid={`sync-source-${config.vertical}`} />
        </div>
      </div>

      <details className="sync-mapping" data-testid={`sync-mapping-${config.vertical}`}>
        <summary>Column mapping ({Object.keys(local.column_mapping || {}).length} fields)</summary>
        <div className="sync-mapping-grid">
          {Object.entries(local.column_mapping || {}).map(([col, header]) => (
            <div key={col}>
              <label>{col.replace(/_/g, " ")}</label>
              <input value={header} onChange={e => patchMap(col, e.target.value)}
                     placeholder={header || col} data-testid={`sync-map-${config.vertical}-${col}`} />
            </div>
          ))}
        </div>
      </details>

      <div className="sync-actions">
        <button className="sync-btn primary" onClick={save} disabled={!dirty || busy === "save"} data-testid={`sync-save-${config.vertical}`}>
          {busy === "save" ? "Saving…" : "Save settings"}
        </button>
        <button className="sync-btn" onClick={() => run(true)} disabled={busy === "preview" || !local.spreadsheet_id} data-testid={`sync-preview-${config.vertical}`}>
          {busy === "preview" ? "Previewing…" : <><Eye size={14} /> Preview</>}
        </button>
        <button className="sync-btn go" onClick={() => run(false)} disabled={busy === "run" || !local.spreadsheet_id} data-testid={`sync-run-${config.vertical}`}>
          {busy === "run" ? "Syncing…" : <><PlayCircle size={14} /> Sync now</>}
        </button>
        {local.spreadsheet_id && (
          <a className="sync-btn ghost" target="_blank" rel="noreferrer" data-testid={`sync-open-${config.vertical}`}
             href={`https://docs.google.com/spreadsheets/d/${local.spreadsheet_id.replace(/^https?:\/\/docs\.google\.com\/spreadsheets\/d\//, '').replace(/\/.*$/, '')}/edit`}>
            <ExternalLink size={12} /> Open sheet
          </a>
        )}
      </div>

      <div className="sync-stats" data-testid={`sync-stats-${config.vertical}`}>
        <div><small>Last sync</small><b>{fmtDT(local.last_sync_at) || "Never"}</b></div>
        <div><small>Next sync</small><b>{local.daily_sync_enabled ? (fmtDT(local.next_sync_at) || "Within 24h") : "Manual only"}</b></div>
        {local.last_stats && (
          <>
            <div><small>Imported</small><b style={{ color: "#166534" }}>{local.last_stats.imported ?? 0}</b></div>
            <div><small>Duplicates</small><b style={{ color: "#B45309" }}>{local.last_stats.duplicates ?? 0}</b></div>
            <div><small>Errors</small><b style={{ color: (local.last_stats.errors > 0 ? "#B91C1C" : "#475569") }}>{local.last_stats.errors ?? 0}</b></div>
            <div><small>Total rows</small><b>{local.last_stats.total_rows ?? 0}</b></div>
          </>
        )}
      </div>

      {result && (
        <div className="sync-result" data-testid={`sync-result-${config.vertical}`}>
          <b>
            {result.stats.dry_run ? <><Eye size={14} /> Preview complete</> : <><CheckCircle2 size={14} /> Sync complete</>}
            {" · "}
            {result.stats.imported} would import · {result.stats.duplicates} duplicates · {result.stats.errors} errors
          </b>
          {result.preview.length > 0 && (
            <ul>
              {result.preview.slice(0, 8).map((p, i) => (
                <li key={i} className={`sync-row-${p.status}`}>
                  Row {p.row}: <b>{p.name || "—"}</b> · {p.mobile || "—"} — <em>{p.status}</em>
                  {p.existing_id && <span> · existing: {p.existing_id}</span>}
                  {p.new_id && <span> · new: {p.new_id}</span>}
                </li>
              ))}
              {result.preview.length > 8 && <li><small>… and {result.preview.length - 8} more rows</small></li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function fmtDT(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
